import React, { useEffect, useRef, useState } from "react";
import Country from "../../modules/base/models/Country";
import Contact from "../../modules/contacts/models/Contact";
import Icon from "../base/Icon";
import PhoneItem from "../../models/PhoneItem";
import { getDefaultActiveCountry, getUSA, openContactActiveJobs, useActiveCountries, useAllCountries, useHub, useScreen } from "../../core/Global";
import { API_ROUTES } from "../../core/Constants";

interface TextBoxProps {
    id?: string
    label?: string
    labelUp?: boolean
    className?: string
    small?: boolean
    solid?: boolean
    name?: string
    parent?: string
    defaultValue?: string
    defaultPhone?: PhoneItem
    required?: boolean
    autoFocus?: boolean
    maxLength?: number
    email?: boolean
    inlineStyle?: boolean
    marginBottom?: number
    noMarginBottom?: boolean
    readOnly?: boolean
    block?: boolean
    tooltip?: string
    autoComplete?: boolean
    reset?: boolean
    countryCodes?: "strict" | "all" | "us-only"
    defaultCountry?: Country
    country?: Country
    tabIndex?: number
    ext?: boolean
    pastJobs?: boolean
    onChange?: (e: PhoneItem) => void
    onSelect?: (contact: Contact) => void
    onComplete?: (e: PhoneItem) => void
    onInComplete?: () => void
    onCountrySelect?: (e: Country) => void
}


export default function PhoneInput(props: TextBoxProps) {

    let searchTimout: NodeJS.Timeout;
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const hub = useHub()
    // "strict" (the default) offers only the account's active countries; "all" is every country.
    const countries = useActiveCountries()
    const countriesAll = useAllCountries()

    const screen = useScreen();

    const fontClass = () => {
        if (props.small) return "fs-8";
        return "fs-7";
    }

    const [inputId] = useState<string>(props.id ?? "txt-" + (Math.floor(Math.random() * 1000000)));

    const [model, modelSet] = useState<Contact[]>()
    const [showList, showListSet] = useState<boolean>()
    const [selectedIndex, selectedIndexSet] = useState<number>(0)
    const [selectedCountry, selectedCountrySet] = useState<Country>()

    const [pastJobs, pastJobsSet] = useState<number>(0)

    const [placeholer, placeholderSet] = useState<string>("(XXX) XXX XXXX");

    const [defaultValueLoaded, defaultValueLoadedSet] = useState<boolean>(false);
    const [defaultCountryLoaded, defaultCountryLoadedSet] = useState<boolean>(false);

    const [value, valueSet] = useState<string>("");
    const [phone, phoneSet] = useState<PhoneItem>();

    const className = () => {
        var cls = "form-control form-inputs";
        if (props.small) cls += " form-control-sm";
        if (props.solid) cls += " form-control-solid";
        if (props.autoFocus) cls += " auto-focus";
        if (props.className) cls += " " + props.className;
        return cls;
    };

    const [ext, extSet] = useState<string>("")
    const [extIsInvalid, extIsInvalidSet] = useState<boolean>(false)
    const extClassName = () => {
        var cls = "form-control form-inputs ms-auto";
        if (props.small) cls += " form-control-sm";
        if (props.solid) cls += " form-control-solid";
        if (extIsInvalid) cls += " is-invalid";
        return cls;
    };

    useEffect(() => {
        phoneSet({
            ...phone,
            extension: ext
        })
    }, [ext])

    const codesClassName = () => {
        var cls = "form-select form-inputs form-select-solid";
        if (props.small) cls += " form-select-sm";
        return cls;
    };

    const inputGroupClassName = () => {
        //"input-group input-group-sm has-validation"
        var cls = "input-group has-validation";
        if (props.small) cls += " input-group-sm";
        if (props.solid) cls += " input-group-solid";
        return cls;
    }

    const keypress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        window.hideFormError(props.name, props.parent);
        if (!/[0-9]/.test(e.key) && e.key != "Enter") {
            window.showFormError(props.name, "Please enter valid latin numbers!", true, props.parent);
            e.preventDefault();
        }
    }


    const keyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {

        // if (e.key == "Backspace") {
        //     console.log("Backspace clear");
        //     e.preventDefault();
        // }

        if (!props.autoComplete) return;

        if (e.key == "ArrowDown") {
            e.preventDefault();
            handleArrowDown();
        }
        else if (e.key == "ArrowUp") {
            e.preventDefault();
            handleArrowUp();
        }
        else if (e.key == "Enter") {
            e.preventDefault();
            handleEnter();
        }
        else if (e.key == "Tab") {
            handleTab();
        }

    }

    const handleArrowDown = () => {
        if (model && model.length > 0) {
            var s = selectedIndex + 1;
            if (s >= model.length) s = model.length - 1;
            selectedIndexSet(s);
        }
    }

    const handleArrowUp = () => {
        var s = selectedIndex - 1;
        if (s < 0) s = 0;
        selectedIndexSet(s);
    }

    const handleEnter = () => {
        selectContact(selectedIndex);
    }

    const handleTab = () => {

    }

    const toRaw = (data?: string) => {
        var ph = "";
        if (!data) return "";
        data = data.replace("+1", "");
        var validNumbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
        for (var i = 0; i < (data.length ?? 0); i++) {
            var c = data[i];
            if (validNumbers.indexOf(c) > -1) ph += c;
        }
        return ph;
    }

    const toFriendly = (value?: string) => {
        if (!value) return "";
        const digits = value.replace(/\D/g, "").slice(0, 10);
        const part1 = digits.slice(0, 3);
        const part2 = digits.slice(3, 6);
        const part3 = digits.slice(6);

        if (digits.length <= 3) return part1;
        if (digits.length <= 6) return `(${part1}) ${part2}`;
        return `(${part1}) ${part2} ${part3}`;
    }


    const selectContact = (index: number) => {
        if (model && model.length > 0) {
            var item = model[index];
            selectedIndexSet(0);
            modelSet([]);
            if (props.onSelect) props.onSelect(item);

            phoneSet(item.phone);
            valueSet(item.phone?.phone ?? "");

            inputRef.current?.blur();
        }
    }


    const fetch = (p?: string, c?: string) => {

        if (!p || p.length < 3) {
            modelSet([]);
            return;
        }

        var q = `?phone=${p}&country=${props.country?.id ?? (getUSA().id)}`;

        hub.Get("/crm/contacts/autocomplete" + q, res => {

            if (res.data) {
                // Reset the highlight to the first row so keyboard nav and the "selected"
                // style don't point at a stale/out-of-range index from a previous search.
                selectedIndexSet(0);
                modelSet(res.data);
            }

        });
    }


    const change = (e: React.ChangeEvent<HTMLInputElement>) => {


        const raw = e.target.value.replace("+1", "").replace(/\D/g, "").slice(0, 10);
        const formatted = toFriendly(raw);

        const cursor = e.target.selectionStart ?? formatted.length;
        const digitsBeforeCursor = e.target.value
            .slice(0, cursor)
            .replace(/\D/g, "").length;

        valueSet(formatted);

        requestAnimationFrame(() => {
            if (inputRef.current) {
                const newCursor = getCursorFromDigits(formatted, digitsBeforeCursor);
                inputRef.current.setSelectionRange(newCursor, newCursor);
            }
        });

        let ph: PhoneItem = {
            ...phone,
            phone: formatted,
            phoneFriendly: toFriendly(formatted)
        };
        phoneSet(ph);

        if (props.autoComplete) {

            if (searchTimout) clearTimeout(searchTimout);
            searchTimout = setTimeout(() => fetch(ph.phone, ph.countryId), 500);
        }

    }

    function getCursorFromDigits(formatted: string, digitIndex: number): number {
        let count = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i])) count++;
            if (count === digitIndex) return i + 1;
        }
        return formatted.length;
    }


    // Keep the keyboard-highlighted row visible while arrowing through a long list.
    useEffect(() => {
        const el = listRef.current?.querySelector<HTMLElement>(`[data-item-index="${selectedIndex}"]`);
        el?.scrollIntoView({ block: "nearest" });
    }, [selectedIndex])

    useEffect(() => {

        if (phone) if (props.onChange) props.onChange(phone);
        if (phone?.phone && phone.phone.length > 8 && props.onComplete) props.onComplete(phone);
        else if (props.onInComplete) props.onInComplete();

    }, [phone])

    useEffect(() => {

        if (countries && (!props.countryCodes || props.countryCodes == "strict")) {
            window.makeCountriesDropDown(`${inputId}-drp-phone-code`,
                countries, props.parent,
                `${inputId}-div-phone-code`, true,
                data => {
                    selectedCountrySet(data);
                    if (props.onCountrySelect) props.onCountrySelect(data);
                    $("#" + inputId).focus();
                });
        }

        if (countriesAll && props.countryCodes == "all") {
            window.makeCountriesDropDown(`${inputId}-drp-phone-code`,
                countriesAll, props.parent,
                `${inputId}-div-phone-code`, true,
                data => {
                    selectedCountrySet(data);
                    if (props.onCountrySelect) props.onCountrySelect(data);
                    $("#" + inputId).focus();
                });
        }


    }, [countries, countriesAll, props.countryCodes])

    useEffect(() => {
        if (selectedCountry) {
            phoneSet({
                ...phone,
                countryId: selectedCountry.id,
                countryCode: "+" + selectedCountry.phoneCode
            })

            window.updateSelect2(`${props.id}-drp-phone-code`, selectedCountry.id);

            if (selectedCountry.alpha2Code?.toLowerCase() == "ge") placeholderSet("(5XX) XXX XXX");
            else placeholderSet("(XXX) XXX XXXX");
        }
    }, [selectedCountry])

    useEffect(() => {

        if (props.reset) {
            phoneSet({
                countryCode: "+1",
                phone: "",
                phoneFriendly: "",
                extension: ""
            })
            valueSet("");
        }

    }, [props.reset])



    useEffect(() => {
        if (props.defaultValue && !defaultValueLoaded) {
            var val = toRaw(props.defaultValue);
            phoneSet({
                countryCode: "+1",
                phone: val,
                phoneFriendly: toFriendly(val)
            })
            valueSet(val);
            defaultValueLoadedSet(true);
        }
    }, [props.defaultValue])



    useEffect(() => {
        if (props.defaultPhone && !defaultValueLoaded && countriesAll) {
            phoneSet(props.defaultPhone);
            countriesAll.forEach(c => {
                if (c.id == props.defaultPhone?.countryId) {
                    selectedCountrySet(c);
                }
            });

            valueSet(toRaw(props.defaultPhone.phone))
            extSet(props.defaultPhone.extension ?? "")
            defaultValueLoadedSet(true);
            defaultCountryLoadedSet(true);
        }
        else if (props.defaultCountry && !defaultCountryLoaded) {
            selectedCountrySet(props.defaultCountry);
            defaultCountryLoadedSet(true);
        }
        else if (props.country) {
            selectedCountrySet(props.country);
        }
        else if (!defaultCountryLoaded) {
            selectedCountrySet(props.countryCodes == "all" || props.countryCodes == "us-only" ? getUSA() : getDefaultActiveCountry());
        }
    }, [props.defaultPhone, props.defaultCountry, props.country, countriesAll])


    useEffect(() => {
        window.makeFormTooltip((props.id ?? inputId) + "-tooltip");
    }, [])

    useEffect(() => {
        if (props.pastJobs) {
            var p = toRaw(phone?.phone)
            if (p.length > 8) {
                hub.Get(API_ROUTES.Jobs + "contactActiveJobs/?phone=" + p,
                    res => {
                        if (res.success) pastJobsSet(res.data)
                        else pastJobsSet(0)
                    })
            }
        }
    }, [props.pastJobs, phone])


    return (
        <div className={"row align-items-center" + (props.noMarginBottom ? "" : " mb-" + (props.marginBottom ?? 5))}>
            {
                props.label &&
                <label className={`col col-12 ${(props.labelUp ? "mb-1" : (props.block ? "col-md-3" : "col-md-2"))} ${fontClass()}`} htmlFor={inputId}>
                    {props.label}
                    {
                        props.tooltip &&
                        <i className="fa-solid fa-circle-question ms-2 form-tooltips"
                            id={(props.id ?? inputId) + "-tooltip"}
                            data-bs-placement="right"
                            data-bs-custom-class="tooltip-dark"
                            title={props.tooltip}></i>
                    }
                    {
                        props.labelUp && <span id={props.name + "-err"} className="small text-danger mx-3 form-error"></span>
                    }

                    {
                        props.pastJobs && pastJobs > 0 ?
                            <span className="badge badge-light-primary float-end cursor-pointer"
                                onClick={e => {
                                    e.preventDefault();
                                    if (phone?.phone) openContactActiveJobs(toRaw(phone.phone));
                                }}>
                                View Past Jobs ({pastJobs})
                            </span>
                            : <></>
                    }
                </label>
            }

            <div className={"col col-12 " + (props.autoComplete ? " position-relative" : "") + (props.labelUp ? "" : (props.block && props.label ? "col-md-9" : props.block ? "col-md-12" : "col-md-6"))}>

                <div className={inputGroupClassName()}>
                    <div id={`${inputId}-div-phone-code`} className="input-group-text"
                        style={{
                            position: "relative"
                        }}>
                        {
                            selectedCountry &&
                            <>
                                <img src={selectedCountry.flagSquare} className="me-2"
                                    style={{
                                        borderRadius: "50%",
                                        height: 15
                                    }} />
                                <div className="fs-8 me-2" style={{
                                    height: 15
                                }}>
                                    +{selectedCountry.phoneCode}
                                </div>
                                {
                                    props.countryCodes != "us-only" &&
                                    <Icon icon="chevron-down" className="fs-8" />
                                }
                            </>
                        }

                        <div style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: "100%"
                        }}>
                            <select id={`${inputId}-drp-phone-code`} className="d-none">
                            </select>
                        </div>
                    </div>

                    <input id={props.id ?? inputId}
                        key={props.id ?? inputId}
                        ref={inputRef}
                        value={toFriendly(value)}
                        name={props.name + "-main"}
                        type="tel"
                        autoComplete="one-time-code"
                        className={className()}
                        onKeyPress={keypress}
                        onKeyDown={keyDown}
                        autoFocus={props.autoFocus}
                        placeholder={placeholer}
                        readOnly={props.readOnly}
                        tabIndex={props.tabIndex}
                        onFocus={e => {
                            if (props.autoComplete) {
                                showListSet(true)
                            }
                        }}
                        onBlur={e => {
                            if (props.autoComplete) {
                                // if (model && model.length > 0) {
                                //     if (model[selectedIndex].phone?.phone == toRaw(e.target.value)) {
                                //         selectContact(selectedIndex)
                                //     }
                                // }
                                setTimeout(() => {
                                    showListSet(false)
                                }, 500);
                            }
                        }}
                        onChange={change}
                    />
                    {
                        props.ext &&
                        <input id={(props.id ?? inputId) + "-ext"}
                            key={(props.id ?? inputId) + "-ext"}
                            name={props.name + "Ext"}
                            type="tel"
                            className={extClassName()}
                            placeholder="Ext."
                            value={ext}
                            onChange={e => extSet(e.target.value)}
                            onKeyPress={e => {
                                if (!/[0-9]/.test(e.key)) {
                                    extIsInvalidSet(true)
                                    e.preventDefault()
                                }
                                else extIsInvalidSet(false)
                            }}
                            style={{
                                maxWidth: 100
                            }}
                        />
                    }
                    <input type="hidden" name={props.name} value={phone?.phone} data-hiddenphone="yes" />
                    <input type="hidden" name={props.name + "CountryId"} value={phone?.countryId} />
                    <input type="hidden" name={props.name + "CountryCode"} value={phone?.countryCode} />
                </div>

                {
                    props.autoComplete && showList && model && model.length > 0 &&
                    <div className="address-field-autocomplete phone-autocomplete" ref={listRef}>
                        {
                            model.map((item, key) => {
                                const addr = item.address?.address;
                                const addressLine = addr?.label
                                    ?? [addr?.houseNumber, addr?.street, addr?.city, addr?.stateCode]
                                        .filter(x => x && x.length > 0).join(", ");
                                return (
                                    <div key={key} data-item-index={key}
                                        className={"address-field-autocomplete-item phone-autocomplete-item" + (selectedIndex == key ? " selected" : "")}
                                        onMouseDown={e => {
                                            e.preventDefault();
                                            selectContact(key);
                                        }}>
                                        <div className="phone-autocomplete-avatar">
                                            <Icon icon="circle-user" type="solid" size={26} />
                                        </div>
                                        <div className="phone-autocomplete-info">
                                            <div className="phone-autocomplete-name">
                                                {item.fullName || "Unknown"}
                                            </div>
                                            <div className="phone-autocomplete-line">
                                                <Icon icon="phone" type="solid" size={11} className="phone-autocomplete-lineicon" />
                                                <span>{item.phone?.phoneFriendly || item.phone?.phone}</span>
                                            </div>
                                            {
                                                item.phoneContact?.phone &&
                                                <div className="phone-autocomplete-line phone-autocomplete-line-muted">
                                                    <Icon icon="mobile" type="solid" size={11} className="phone-autocomplete-lineicon" />
                                                    <span>{item.phoneContact?.phoneFriendly || item.phoneContact?.phone}</span>
                                                </div>
                                            }
                                            {
                                                addressLine &&
                                                <div className="phone-autocomplete-line phone-autocomplete-line-muted">
                                                    <Icon icon="location-dot" type="solid" size={11} className="phone-autocomplete-lineicon" />
                                                    <span className="phone-autocomplete-address">{addressLine}</span>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                }
            </div>
            {
                props.block && props.label && false &&
                <div className="col col-12 col-md-3"></div>
            }
            {
                !props.labelUp &&
                <div className={"col col-12 " + (props.block ? "col-md-9" : "col-md-4")}>
                    <span id={props.name + "-err"} className="small text-danger mx-3 form-error"></span>
                </div>
            }



        </div>
    )

}